import React, { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../header";
import LAWarningPopup from "../LAWarningPopup";
import { STRINGS } from "../../constant";
import ChangeExam from "../popup/ChangeExam";
import ChangeFont from "../popup/ChangeFont";
import { IMAGES, SVG_IMAGES } from "../../assets";
import { ThemeContext } from "../../context/ThemeProvider";
import { USER_DEAFULT_KEY } from "../../constant/event";
import Preapare from "./Preapare";
import { BundleContext } from "../../context/BundleProvider";
import { auth } from "../../api/config";
import clsx from "clsx";
import { APP_BUNDLES } from "../../../app_constant";
import { AuthContext } from "../../context/AuthProvider";
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { createLogger } from "../../utils/logger";

// Import Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faChartPie,
  faChartColumn,
  faHistory,
  faExchangeAlt,
  faClock,
  faBook,
  faGraduationCap,
  faClipboardList,
  faLanguage,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import useFeatureList from "../../hooks/useFeatureList";
import SupportEmailForm from "../SupportEmailForm";
import { getAppLogo } from "../../api/theme_settings";

const MainLayout = () => {
  const { isGlossary, isAcronyms, isFlashcards } = useFeatureFlags();
  const [layoutState, setLayoutState] = useState({
    isReset: false,
    changeFont: false,
  });
  const { selectedCertificate } = useContext(BundleContext);
  const { theme, currentTheme, setCurrentTheme } = useContext(ThemeContext);
  const [availableExams, setAvailableExams] = useState([]);
  const [daysLeft, setDaysLeft] = useState(null);
  const { mySubscription, loggingEnabled } = useContext(AuthContext);
  const [logo, setLogo] = useState(null);
  
  const logger = createLogger(loggingEnabled);

  const [showPreaperScreen, setShowPreapareScreen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [isTestPage, setIsTestPage] = useState(false);
  const [isTestExitPopup, setIsTestExitPopup] = useState(false);
  const [navigatePath, setNavigatePath] = useState(null);
  const [active, setActive] = useState(location?.pathname);
  const [showPopup, setShowPopup] = useState(false);
  const [switchPopup, setSwitchPopup] = useState(false);
  const featureList = useFeatureList();

  // Update active page state
  useEffect(() => {
    setIsTestPage(location.pathname === "/test");
    setActive(location?.pathname);
  }, [location.pathname]);

  // Load exams effect
  useEffect(() => {
    const loadExams = async () => {
      try {
        logger.log('Loading exams...');
        const exams = await APP_BUNDLES;
        logger.log('Available exams:', exams);
        logger.log('Number of exams:', exams.length);
        setAvailableExams(exams);
      } catch (error) {
        logger.error('Error loading exams:', error);
      }
    };
    loadExams();
  }, []);

  // Add effect to log when availableExams changes
  useEffect(() => {
    logger.log('availableExams state updated:', availableExams);
    logger.log('Number of exams in state:', availableExams.length);
  }, [availableExams]);

  // Calculate subscription days left
  useEffect(() => {
    if (mySubscription?.subscription_expiration) {
      const endDate = new Date(mySubscription.subscription_expiration.toDate());
      const today = new Date();
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      setDaysLeft(diffDays > 0 ? diffDays : 0);
    } else {
      setDaysLeft(null);
    }
  }, [mySubscription]);

  useEffect(() => {
    getAppLogo(currentTheme).then(setLogo);
  }, [currentTheme]);

  // Theme toggle function
  const onToggleTheme = () => {
    const body = document.body;
    const newTheme = currentTheme === "light" ? "dark" : "light";
    localStorage.setItem("Theme", newTheme);
    setCurrentTheme(newTheme);
    body.classList.toggle("dark", newTheme === "dark");
    getAppLogo(currentTheme);
  };

  // Empty effect from original code
  useEffect(() => { }, [location]);

  // Define sidebar menu - Main Navigation
  const sidebarMenu = [
    {
      name: "Home",
      path: "/home",
      active: active === "/home",
      flag: true,
      icon: faHome,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      active: active === "/dashboard",
      flag: true,
      icon: faChartColumn,
    },
    {
      name: "History",
      path: "/history",
      active: active === "/history",
      flag: featureList?.history.view_test_history,
      icon: faHistory,
    },
  ];

  // Update the resourcesMenu to use feature flags
  const resourcesMenu = [
    {
      name: "Glossary",
      path: "/glossary",
      active: active === "/glossary",
      flag: isGlossary,
      icon: faBook,
    },
    {
      name: "Acronyms",
      path: "/acronymns",
      active: active === "/acronymns",
      flag: isAcronyms,
      icon: faLanguage,
    },
  ];

  // Update the studyToolsMenu to use feature flags
  const studyToolsMenu = [
    {
      name: "Study Questions",
      path: "/studyQuestion",
      active: active === "/studyQuestion",
      flag: true,
      icon: faGraduationCap,
    },
    {
      name: "Practice Tests",
      path: "/practicetest",
      active: active === "/practicetest",
      flag: true,
      icon: faClipboardList,
    },
    {
      name: "Flashcards",
      path: "/flashcard",
      active: active === "/flashcard",
      flag: isFlashcards,
      icon: faBook,
    },
  ];
  const customTestMenu = [
    {
      name: "Create Custom Test",
      path: "/customtest",
      active: active === "/customtest",
      flag: true,
      icon: faPenToSquare,
    }
  ];

  // Handle sidebar navigation
  const handleSidebarClick = (path) => {
    setNavigatePath(path);
    if (isTestPage && path !== "/test") {
      setIsTestExitPopup(true);
    } else {
      setIsTestExitPopup(false);
      setActive(path);
      navigate(path);
    }
  };

  // Handle browser back button
  useEffect(() => {
    const handleBackButton = () => {
      if (location?.pathname === "/test") {
        setShowPopup(true);
        setIsTestExitPopup(true);
      }
      window.history.pushState(null, document.title, window.location.href);
    };

    window.onpopstate = handleBackButton;
    return () => {
      window.onpopstate = null;
    };
  }, []);

  // Get theme-based colors
  const getSidebarBg = () => {
    return currentTheme === 'light'
      ? theme?.background?.sidebarLight
      : theme?.background?.sidebarDark;
  };

  const getContentBg = () => {
    return currentTheme === 'light'
      ? theme?.background?.light
      : theme?.background?.dark;
  };

  const getTextColor = () => {
    return currentTheme === 'light'
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const getPrimaryColor = () => {
    return currentTheme === 'light'
      ? theme?.primary?.light
      : theme?.primary?.dark;
  };

  const getSecondaryColor = () => {
    return currentTheme === 'light'
      ? theme?.secondary?.light
      : theme?.secondary?.dark;
  };

  const getBorderColor = () => {
    return currentTheme === 'light'
      ? theme?.border?.light
      : theme?.border?.dark;
  };

  const getMutedTextColor = () => {
    return theme?.text?.muted;
  };

  // Animation variants for menu items
  const menuItemVariants = {
    inactive: { scale: 1 },
    active: { scale: 1.05, transition: { type: "spring", stiffness: 300, damping: 15 } },
    hover: { scale: 1.02, transition: { type: "spring", stiffness: 400, damping: 10 } }
  };

  const renderMenuItem = (item) => {
    return (
      item.flag && (
        <motion.div
          key={item.path}
          initial="inactive"
          animate={item.active ? "active" : "inactive"}
          whileHover="hover"
          variants={menuItemVariants}
          onClick={() => handleSidebarClick(item.path)}
          className={`cursor-pointer w-full h-auto  py-4 max-[1366px]:py-3 px-4 flex justify-start items-center ${item.active
              ? 'bg-opacity-10'
              : 'hover:bg-opacity-5'
            }`}
          style={{
            backgroundColor: item.active
              ? currentTheme === 'light' ? `${getPrimaryColor()}50` : `${getPrimaryColor()}10`
              : 'transparent'
          }}
        >
          <div className="w-[20px]  flex items-center justify-center mr-3">
            <FontAwesomeIcon
              icon={item.icon}
              className="text-lg"
              style={{
                color: item.active
                  ? getPrimaryColor()
                  : getTextColor()
              }}
            />
          </div>
          <p
            className="text-left text-sm font-medium transition-colors duration-200"
            style={{
              color: item.active
                ? getPrimaryColor()
                : getTextColor()
            }}
          >
            {item.name}
          </p>
        </motion.div>
      )
    );
  };


  const [isDialogOpen, setDialogOpen] = useState();
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  return (
    <main
      className="min-h-screen transition-colors duration-300"
      style={{ background: getContentBg() }}
    >
      {/* Sidebar - for both mobile and desktop with responsive classes */}
      <aside
        className="sidebar-container flex flex-row lg:flex-col fixed h-[70px] lg:h-full w-full lg:w-[200px] bottom-0 left-0 right-0 lg:top-0 lg:left-0 lg:bottom-0 z-40 transition-all duration-300"
        style={{
          background: getSidebarBg(),
          borderTop: currentTheme === 'light'
            ? `1px solid ${getBorderColor()}`
            : 'none',
          borderRight: currentTheme === 'light'
            ? `1px solid ${getBorderColor()}`
            : 'none'
        }}
      >
        
        {/* Desktop Sidebar Content */}
        <div className="hidden lg:flex lg:flex-col w-full h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-center pt-8 pb-12">
            <img
              className="h-[50px]"
              src={logo}
              alt="logo"
            />
          </div>

          {/* Main Navigation Section */}
          <div className="pt-2 pb-3">
            {sidebarMenu.map(renderMenuItem)}
          </div>

          {/* Study Tools Section */}
          <div className="mt-4 border-t pt-4" style={{ borderColor: `${getTextColor()}50` }}>
            <div className="px-4 mb-2">
              <span className="text-xs uppercase font-semibold tracking-wider" style={{ color: `${getTextColor()}80` }}>
                Study Tools
              </span>
            </div>
            {studyToolsMenu.map(renderMenuItem)}
          </div>

          {/* Custom Test Section */}
          <div className="mt-4 border-t pt-4" style={{ borderColor: `${getTextColor()}50` }}>

            {customTestMenu.map(renderMenuItem)}
          </div>


          {/* Resources Section */}
          <div className="mt-4 border-t pt-4" style={{ borderColor: `${getTextColor()}50` }}>
            <div className="px-4 mb-2">
              <span className="text-xs uppercase font-semibold tracking-wider" style={{ color: `${getTextColor()}80` }}>
                Resources
              </span>
            </div>
            {resourcesMenu.map(renderMenuItem)}
          </div>

          {/* Subscription Info */}
          {daysLeft !== null && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: `${getTextColor()}50` }}>
              <div className="flex flex-col items-center justify-center space-y-1">
                <div className="flex items-center space-x-2" style={{ color: `${getTextColor()}80` }}>
                  <FontAwesomeIcon icon={faClock} className="text-base" />
                  <span className="text-sm font-medium">
                    {daysLeft > 0 ? "Access expires in" : "Access expired"}
                  </span>
                </div>
                {daysLeft > 0 && (
                  <div className="text-lg font-semibold" style={{ color: getPrimaryColor() }}>
                    {daysLeft} days
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Powered by Logo */}
          <div className="absolute bottom-4 left-0 right-0 px-4 py-2 text-center">
            <div className="flex flex-col items-center justify-center">
              <span className="text-xs mb-1" style={{ color: `${getTextColor()}80` }}>Powered by</span>
              <img
                src={IMAGES.logo_prepare}
                alt="Prepare Logo"
                className="h-6 w-auto object-contain"
              />
            </div>
          </div>

        </div>

        {/* Mobile Bottom Navigation */}
        <div className="flex lg:hidden flex-row w-full">
          {sidebarMenu.map((item, idx) => (
            item.flag && (
              <motion.div
                key={idx}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSidebarClick(item.path)}
                className="w-[33%] h-full py-3 flex justify-center items-center flex-col"
              >
                <div className="flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="text-xl"
                    style={{
                      color: item.active
                        ? getPrimaryColor()
                        : getTextColor()
                    }}
                  />
                </div>
                <p
                  className="mt-1 text-center text-[10px] font-medium"
                  style={{
                    color: item.active
                      ? getPrimaryColor()
                      : getTextColor()
                  }}
                >
                  {item.name}
                </p>
              </motion.div>
            )
          ))}

          {/* Switch Exam Button for Mobile */}

          {featureList?.global.switch_exam && <>

            {availableExams.length > 1 && (
              <motion.div
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (location?.pathname === "/test") {
                    setSwitchPopup(true);
                  } else {
                    setLayoutState((prev) => ({ ...prev, isReset: true }));
                  }
                }}
                className="w-[33%] h-full py-3 flex justify-center items-center flex-col"
              >
                <div
                  className="flex items-center justify-center h-7 w-7 rounded-full"
                  style={{ background: getPrimaryColor() }}
                >
                  <FontAwesomeIcon
                    icon={faExchangeAlt}
                    className="text-sm"
                    style={{ color: '#ffffff' }}
                  />
                </div>
                <p className="mt-1 text-center text-[10px] font-medium" style={{ color: getTextColor() }}>
                  Switch Exam
                </p>
              </motion.div>
            )}
          </>}

        </div>
      </aside>

      {/* Main Content Area */}
      <div className="lg:pl-[200px] pl-[0px] lg:pb-[0px] pb-[70px] min-h-screen transition-all duration-300">
        <Header
          onChangeFont={() => setLayoutState((prev) => ({ ...prev, changeFont: true }))}
          onThemeChange={() => onToggleTheme()}
          onProfile={() => handleSidebarClick("/profile")}
          onContactSupport={() => handleOpenDialog()}
        />
        <div className="main-content w-full pt-6 pb-20 px-4" style={{ backgroundColor: currentTheme === 'light' ? theme?.background?.light : theme?.background?.dark }}>
          <Outlet />
        </div>
      </div>


      {isDialogOpen && (
        <SupportEmailForm
          handleCloseDialog={handleCloseDialog}
          isLoading={false}
          setLoading={() => { }}
        />
      )}

      {/* Popups/Modals */}
      <AnimatePresence>
        {layoutState?.isReset && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ChangeExam
              state={layoutState}
              onCancle={() => {
                setLayoutState((prev) => ({ ...prev, isReset: false }));
              }}
              onPress={(e, index) => { }}
              options={["change"]}
              title={STRINGS.change_bundle}
              desc={STRINGS.change_bundle_messge}
            />
          </motion.div>
        )}

        {layoutState?.changeFont && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ChangeFont
              onChangeFont={() => {
                setLayoutState((prev) => ({
                  ...prev,
                  changeFont: !layoutState?.changeFont,
                }));
              }}
            />
          </motion.div>
        )}

        {isTestExitPopup && isTestPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LAWarningPopup
              options={[STRINGS.no, STRINGS.yes]}
              title={STRINGS.test_exit_warning}
              onCancle={() => {
                setIsTestExitPopup(false);
              }}
              onPress={(e, index) => {
                if (index === 0) {
                  setIsTestExitPopup(false);
                } else {
                  setIsTestPage(false);
                  handleSidebarClick(location.pathname);
                  navigate(navigatePath);
                  setActive(navigatePath);
                }
              }}
            />
          </motion.div>
        )}

        {isTestExitPopup && showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LAWarningPopup
              options={[STRINGS.no, STRINGS.yes]}
              title={STRINGS.test_exit_warning}
              onCancle={() => {
                setIsTestExitPopup(false);
              }}
              onPress={(e, index) => {
                if (index === 0) {
                  setIsTestExitPopup(false);
                  handleSidebarClick(location.pathname);
                } else {
                  setIsTestPage(false);
                  handleSidebarClick(location.pathname);
                  navigate(navigatePath);
                  setActive(navigatePath);
                }
              }}
            />
          </motion.div>
        )}

        {switchPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LAWarningPopup
              options={[STRINGS.no, STRINGS.yes]}
              title={STRINGS.test_exit_warning}
              onCancle={() => {
                setSwitchPopup(false);
              }}
              onPress={(e, index) => {
                if (index === 0) {
                  setSwitchPopup(false);
                } else {
                  setLayoutState((prev) => ({ ...prev, isReset: true }));
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default MainLayout;